import {
    getStep,
    listSteps
} from './queries';
import {
    createStep,
    deleteStep,
    updateStep,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';

import {
    CreateStep,
    DeleteStep,
    GetStep,
    ListSteps,
    Step,
    UpdateStep
} from "../types.ts";
// FNP-917: Playbooks, Phases and Steps live in flexinet-workflows-api, NOT
// console-api. A bare generateClient() uses the DEFAULT Amplify endpoint
// (console-api), whose schema has zero Playbook/Phase/Step types — so every
// request failed schema validation IN THE BROWSER before reaching a server.
// Verified against the deployed dev console-api schema: 0 occurrences of
// "Playbook", "listPhases" and "listSteps"; "listAccounts" present.
//
// workflowsApiClient already existed, correctly configured per stage. This
// module simply was not using it.
import {workflowsApiClient} from "../../workflows-api-integration/client.ts";


const client = workflowsApiClient;

// Token-paginated list. Returns the raw page envelope — `nextToken` is opaque
// and is handed straight back on the next call, never parsed or constructed.
//
// ⚠️ The defensive position sort that used to live here was removed, not moved.
// It sorted the ONE page this call returns, which under token pagination is a
// window, not the list — sorting a window and calling the result ordered is the
// misleading half of the bug. Steps are ordered by `position` for real in
// useSteps, over every page loaded so far, and the backend index
// `record-type-position-index` already returns each page position-ascending.
export const fetchRecords = async (query: {
    filter?: object;
    limit?: number;
    nextToken?: string | null;
}) => {
    const {filter, limit, nextToken} = query;
    const response = await client.graphql<GraphQLQuery<ListSteps>>({
                                                                       query: listSteps,
                                                                       variables: {
                                                                           filter,
                                                                           limit,
                                                                           nextToken,
                                                                       },
                                                                   });
    return {
        items: response.data?.listSteps.items ?? [],
        nextToken: response.data?.listSteps.nextToken,
    };
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetStep>>({
                                                                     query: getStep,
                                                                     variables: {id: id},
                                                                 });
    return response.data?.getStep;
};

// FNP-999: `parameters` is AWSJSON — a string holding JSON.
//
// ⚠️ The workflows-api `updateStep` resolver builds its DynamoDB expression as
// `if (v) SET else REMOVE`, so an EMPTY STRING is not "leave it alone" — it is
// a REMOVE that deletes the attribute. An omitted key is untouched. So an
// empty/whitespace-only box must send `undefined`, never `''`.
export const normalizeParameters = (record: Step): Step => {
    const raw = record.parameters;

    if (typeof raw !== 'string' || raw.trim() === '') {
        // Omit the key entirely: DynamoDB leaves any existing value in place.
        const {parameters: _omitted, ...rest} = record;
        return rest as Step;
    }

    return {...record, parameters: raw.trim()};
};

export const add = async (payload: object) => {
    // FNP-999: same empty-string guard as update — see normalizeParameters.
    const input = normalizeParameters(payload as Step);
    const response = await client.graphql<GraphQLQuery<CreateStep>>({
                                                                        query: createStep,
                                                                        variables: {input},
                                                                    });
    return response.data?.createStep;
};

export const update = async (_id: string, record: Step) => {

    record = normalizeParameters(record);

    delete record.created;
    delete record.updated;
    delete record.createdByUsername;
    // `phase` is a resolver-populated nested object and is not accepted as
    // input on UpdateStepInput — strip it before sending the mutation.
    delete (record as { phase?: unknown }).phase;
    delete (record as { createdBy?: unknown }).createdBy;

    const response = await client.graphql<GraphQLQuery<UpdateStep>>({
                                                                        query: (updateStep),
                                                                        variables: {input: record},
                                                                    });
    return response.data?.updateStep;
};

export const deleteRecord = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<DeleteStep>>({
                                                                        query: (deleteStep),
                                                                        variables: {input: {id: id}},
                                                                    });
    return response.data?.deleteStep;
};
